import "flatpickr/dist/flatpickr.min.css";
import { useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import { Modal } from "react-bootstrap";
import { IRestaurantDetail, RestaurantList } from "../models/IRestaurantDetail";
import { getRestaurantById, getRestaurantList } from "../services/restaurant";
import { IBill } from "../models/IBill";
import { postBill, getBillsByRestaurant, putBill } from "../services/bill";
import { IExpense, IExpenseFilter } from "../models/IExpense";
import { getExpenses } from "../services/expense";
import { getUserToken } from "../utils/Util";
import AddExpenseModal from "../components/AddExpenseModal";
import ExpenseDetailModal from "../components/ExpenseDetailModal";
import VendorManagerModal from "../components/VendorManagerModal";
import ExpenseSummary from "../components/ExpenseSummary";

const CURRENCY_ID = "4263309b-97c8-49ff-94a3-cd2f39bb2c90";

const MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

type TabId = "add" | "list";

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// ─── Restoran Seçici ─────────────────────────────────────────────────────────
function RestaurantSelector({
  restaurantList, selectedId, onChange, selectedRestaurant,
}: {
  restaurantList?: RestaurantList;
  selectedId: string;
  onChange: (id: string) => void;
  selectedRestaurant?: IRestaurantDetail;
}) {
  return (
    <div className="row align-items-center mb-4 g-3">
      <div className="col-md-5">
        <label className="form-label text-muted small fw-semibold mb-1 text-uppercase" style={{ letterSpacing: "0.05em" }}>
          <i className="bi bi-building me-1"></i>Restoran
        </label>
        <select
          className="form-select form-select-lg"
          style={{ borderRadius: "12px", border: "1.5px solid #dee2e6", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", fontWeight: 500 }}
          value={selectedId}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="" disabled>Restoran seçiniz...</option>
          {restaurantList?.items.map((item) => (
            <option key={item.id} value={item.id}>{item.restourantName}</option>
          ))}
        </select>
      </div>
      {selectedRestaurant && (
        <div className="col-md-7">
          <div
            className="d-flex align-items-center p-3 rounded-3"
            style={{ background: "linear-gradient(135deg,#fff 0%,#f8f9fa 100%)", border: "1.5px solid #e9ecef", boxShadow: "0 4px 16px rgba(0,0,0,0.07)" }}
          >
            <img
              src={"https://mutfacik.s3.eu-central-1.amazonaws.com/" + selectedRestaurant.restourantLogoPath}
              style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "12px", border: "2px solid #fff", boxShadow: "0 2px 8px rgba(0,0,0,0.14)", flexShrink: 0 }}
              alt={selectedRestaurant.restourantName}
            />
            <div className="ms-3">
              <div className="fw-bold" style={{ fontSize: "1rem" }}>{selectedRestaurant.restourantName}</div>
              <span
                className="badge mt-1"
                style={{
                  background: selectedRestaurant.restourantIsEnabled ? "#dcfce7" : "#fee2e2",
                  color: selectedRestaurant.restourantIsEnabled ? "#166534" : "#991b1b",
                  fontSize: "0.72rem", borderRadius: "20px", padding: "3px 10px",
                }}
              >
                <i className={`bi ${selectedRestaurant.restourantIsEnabled ? "bi-door-open-fill" : "bi-door-closed-fill"} me-1`}></i>
                {selectedRestaurant.restourantIsEnabled ? "Açık" : "Kapalı"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Fatura Kartı ─────────────────────────────────────────────────────────────
function BillCard({ bill, onEdit }: { bill: IBill; onEdit: () => void }) {
  return (
    <div className="p-3 rounded-3 mb-2" style={{ background: "#fff", border: "1.5px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div className="d-flex gap-4 align-items-start flex-wrap">
          <div>
            <div className="text-muted" style={{ fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase" }}>Fatura No</div>
            <div className="fw-bold" style={{ fontSize: "1rem", color: "#1d4ed8" }}>#{bill.billNo}</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase" }}>Tarih</div>
            <div style={{ fontSize: "0.88rem", color: "#374151" }}>{formatDate(bill.billDate)}</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase" }}>Tutar</div>
            <div className="fw-bold" style={{ fontSize: "1rem", color: "#e67e00" }}>{bill.billAmount} ₺</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase" }}>Açıklama</div>
            <div style={{ fontSize: "0.88rem", color: "#374151" }}>{bill.billDescription || "—"}</div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          <span className="badge" style={{ background: bill.billIsPaid ? "#dcfce7" : "#fef9c3", color: bill.billIsPaid ? "#166534" : "#854d0e", borderRadius: "20px", fontSize: "0.72rem", padding: "4px 10px" }}>
            <i className={`bi ${bill.billIsPaid ? "bi-check-circle-fill" : "bi-clock"} me-1`}></i>
            {bill.billIsPaid ? "Ödendi" : "Ödenmedi"}
          </span>
          <span className="badge" style={{ background: bill.billIsEnabled ? "#eff6ff" : "#f3f4f6", color: bill.billIsEnabled ? "#1d4ed8" : "#6b7280", borderRadius: "20px", fontSize: "0.72rem", padding: "4px 10px" }}>
            {bill.billIsEnabled ? "Aktif" : "Pasif"}
          </span>
          <button
            onClick={onEdit}
            className="btn btn-sm"
            style={{ borderRadius: "8px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600, fontSize: "0.78rem" }}
          >
            <i className="bi bi-pencil-fill me-1"></i>Düzenle
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Şahıs Faturası Kartı ──────────────────────────────────────────────────────
function ExpenseCard({ exp, onClick }: { exp: IExpense; onClick: () => void }) {
  return (
    <div className="col-12 col-md-6 col-xl-4">
      <div className="h-100 p-3" role="button" onClick={onClick}
        style={{ borderRadius: "14px", border: "1.5px solid #e2e8f0", background: "#fff", cursor: "pointer", transition: "box-shadow 0.15s,border-color 0.15s", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
        onMouseEnter={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.boxShadow = "0 4px 16px rgba(99,102,241,0.18)"; el.style.borderColor = "#a5b4fc"; }}
        onMouseLeave={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; el.style.borderColor = "#e2e8f0"; }}>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="px-3 py-1 rounded-pill fw-bold"
            style={{ background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", fontSize: "1rem", letterSpacing: "0.02em" }}>
            {exp.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {exp.currencyDisplayText}
          </div>
          <span className="badge"
            style={{ background: exp.isReimbursed ? "#dcfce7" : "#fef3c7", color: exp.isReimbursed ? "#166534" : "#92400e", borderRadius: "8px", padding: "5px 10px", fontWeight: 600, fontSize: "0.72rem" }}>
            {exp.isReimbursed ? "Ödendi" : "Ödenmedi"}
          </span>
        </div>
        <div className="fw-semibold text-dark mb-1" style={{ fontSize: "0.9rem" }}>
          <i className="bi bi-person-fill me-1 text-primary"></i>{exp.spentByUserName}
        </div>
        {exp.vendorName && (
          <div className="mb-1" style={{ fontSize: "0.79rem", color: "#b45309" }}>
            <i className="bi bi-building me-1"></i>{exp.vendorName}
          </div>
        )}
        {exp.description && (
          <div className="text-muted mb-2" style={{ fontSize: "0.83rem", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
            {exp.description}
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center mt-2 pt-1" style={{ borderTop: "1px solid #f1f5f9" }}>
          <span style={{ fontSize: "0.79rem", color: "#94a3b8" }}>
            <i className="bi bi-calendar3 me-1"></i>
            {new Date(exp.spentAt).toLocaleDateString("tr-TR")}
          </span>
          {exp.documents.length > 0 && (
            <span style={{ fontSize: "0.78rem", color: "#94a3b8" }}>
              <i className="bi bi-paperclip me-1"></i>{exp.documents.length} belge
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────
function EditBillModal({
  bill, restaurantId, onClose, onSaved,
}: {
  bill: IBill | null;
  restaurantId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [isPaid, setIsPaid] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [payDate, setPayDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (bill) {
      setIsPaid(bill.billIsPaid);
      setIsEnabled(bill.billIsEnabled);
      setPayDate(new Date());
      setError(null);
    }
  }, [bill]);

  const handleSave = () => {
    if (!bill) return;
    setLoading(true);
    setError(null);
    const pd = new Date(payDate);
    pd.setHours(0, 0, 0, 0);
    putBill({ billNo: bill.billNo, restourantId: restaurantId, billIsPaid: isPaid, billPayDate: pd.toISOString(), billIsEnabled: isEnabled })
      .then(() => { onSaved(); onClose(); })
      .catch((err) => {
        const desc = err?.response?.data?.[0]?.description ?? err?.response?.data?.description;
        setError(desc ?? "Güncelleme sırasında bir hata oluştu.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal show={!!bill} onHide={onClose} centered>
      <Modal.Header closeButton style={{ background: "#eff6ff", borderBottom: "2px solid #bfdbfe" }}>
        <div>
          <div className="text-muted" style={{ fontSize: "0.78rem" }}>Fatura Düzenle</div>
          <div className="fw-bold" style={{ fontSize: "1.05rem", color: "#1d4ed8" }}>
            <i className="bi bi-receipt me-2"></i>#{bill?.billNo}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: "20px 24px" }}>
        <div className="row g-2 mb-4">
          {[
            { label: "Tarih",    value: bill ? formatDate(bill.billDate) : "—" },
            { label: "Tutar",    value: bill ? `${bill.billAmount} ₺` : "—" },
            { label: "Açıklama", value: bill?.billDescription || "—" },
          ].map((item) => (
            <div key={item.label} className="col-sm-4">
              <div className="p-2 rounded-2" style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                <div className="text-muted" style={{ fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase" }}>{item.label}</div>
                <div className="fw-semibold" style={{ fontSize: "0.88rem", color: "#111827" }}>{item.value}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="d-flex flex-column gap-3">
          <div className="d-flex align-items-center justify-content-between p-3 rounded-3" style={{ background: "#f9fafb", border: "1.5px solid #e5e7eb" }}>
            <div>
              <div className="fw-semibold" style={{ fontSize: "0.88rem" }}>Ödeme Durumu</div>
              <div className="text-muted" style={{ fontSize: "0.78rem" }}>{isPaid ? "Ödendi olarak işaretli" : "Ödenmedi"}</div>
            </div>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch" checked={isPaid} onChange={(e) => setIsPaid(e.target.checked)} style={{ width: "2.5rem", height: "1.3rem", cursor: "pointer" }} />
            </div>
          </div>

          {isPaid && (
            <div>
              <label className="form-label small fw-semibold text-muted mb-1">
                <i className="bi bi-calendar-check me-1"></i>Ödeme Tarihi
              </label>
              <Flatpickr
                value={payDate}
                onChange={(dates) => dates[0] && setPayDate(dates[0])}
                options={{ dateFormat: "d-m-Y", disableMobile: true }}
                className="form-control"
                style={{ borderRadius: "10px", border: "1.5px solid #dee2e6" }}
              />
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between p-3 rounded-3" style={{ background: "#f9fafb", border: "1.5px solid #e5e7eb" }}>
            <div>
              <div className="fw-semibold" style={{ fontSize: "0.88rem" }}>Aktiflik</div>
              <div className="text-muted" style={{ fontSize: "0.78rem" }}>{isEnabled ? "Fatura aktif" : "Fatura pasif"}</div>
            </div>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} style={{ width: "2.5rem", height: "1.3rem", cursor: "pointer" }} />
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-3 p-2 rounded-2" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: "0.82rem" }}>
            <i className="bi bi-exclamation-circle me-1"></i>{error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ background: "#f9fafb", borderTop: "1.5px solid #e5e7eb", padding: "12px 24px" }}>
        <button onClick={onClose} className="btn" style={{ borderRadius: "10px", background: "#f3f4f6", color: "#374151", border: "1.5px solid #e5e7eb", fontWeight: 600 }}>
          <i className="bi bi-x-lg me-1"></i>İptal
        </button>
        <button
          onClick={handleSave}
          disabled={loading}
          className="btn"
          style={{ borderRadius: "10px", background: "linear-gradient(135deg,#3b82f6,#1d4ed8)", color: "#fff", fontWeight: 600, border: "none", boxShadow: "0 3px 10px rgba(59,130,246,0.3)" }}
        >
          {loading
            ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Kaydediliyor...</>
            : <><i className="bi bi-check-lg me-1"></i>Kaydet</>}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────
function AddBillPage() {
  const [restaurantList, setRestaurantList] = useState<RestaurantList>();
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>("");
  const [selectedRestaurant, setSelectedRestaurant] = useState<IRestaurantDetail>();
  const [activeTab, setActiveTab] = useState<TabId>("add");

  // Fatura Ekle
  const [billAmount, setBillAmount] = useState<string>("");
  const [billDescription, setBillDescription] = useState<string>("");
  const [billDate, setBillDate] = useState<Date>(new Date());
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState(false);

  // Fatura Listesi
  const [month, setMonth] = useState<number | null>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [filterPaid, setFilterPaid] = useState<string>("");
  const [filterEnabled, setFilterEnabled] = useState<string>("");
  const [billList, setBillList] = useState<IBill[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Edit
  const [editBill, setEditBill] = useState<IBill | null>(null);

  // Sayfa sekmeleri
  const [pageTab, setPageTab] = useState<"kurumsal" | "sahis">("kurumsal");
  const myUserId = getUserToken()?.userId ?? null;

  // Şahıs Faturaları
  const [expenses, setExpenses] = useState<IExpense[]>([]);
  const [expLoading, setExpLoading] = useState(false);
  const [expError, setExpError] = useState<string | null>(null);
  const [expFilterMode, setExpFilterMode] = useState<"all" | "mine">("all");
  const [expFilterReimbursed, setExpFilterReimbursed] = useState<"" | "true" | "false">("");
  const [expFilterFrom, setExpFilterFrom] = useState("");
  const [expFilterTo, setExpFilterTo] = useState("");
  const [selectedExpense, setSelectedExpense] = useState<IExpense | null>(null);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showVendorManager, setShowVendorManager] = useState(false);

  useEffect(() => {
    getRestaurantList().then((res) => { if (res.data) setRestaurantList(res.data); });
  }, []);

  useEffect(() => {
    if (selectedRestaurantId) {
      getRestaurantById(selectedRestaurantId).then((res) => { if (res.data) setSelectedRestaurant(res.data); });
    } else {
      setSelectedRestaurant(undefined);
    }
  }, [selectedRestaurantId]);

  const handleAddBill = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRestaurantId) return;
    setAddLoading(true);
    setAddError(null);
    setAddSuccess(false);
    const d = new Date(billDate);
    d.setHours(0, 0, 0, 0);
    postBill({ restourantId: selectedRestaurantId, currencyId: CURRENCY_ID, billAmount: Number(billAmount), billDescription, billDate: d.toISOString() })
      .then(() => { setAddSuccess(true); setBillAmount(""); setBillDescription(""); setBillDate(new Date()); })
      .catch((err) => {
        const desc = err?.response?.data?.[0]?.description ?? err?.response?.data?.description;
        setAddError(desc ?? "Fatura eklenirken bir hata oluştu.");
      })
      .finally(() => setAddLoading(false));
  };

  const fetchBills = () => {
    if (!selectedRestaurantId) return;
    setListLoading(true);
    setListError(null);
    const paid    = filterPaid    !== "" ? filterPaid    === "true" : undefined;
    const enabled = filterEnabled !== "" ? filterEnabled === "true" : undefined;
    getBillsByRestaurant(selectedRestaurantId, month, year, paid, enabled)
      .then((res) => setBillList(res.data ?? []))
      .catch((err) => {
        const desc = err?.response?.data?.[0]?.description ?? err?.response?.data?.description;
        setListError(desc ?? "Faturalar yüklenemedi.");
        setBillList([]);
      })
      .finally(() => setListLoading(false));
  };

  const fetchExpenses = () => {
    setExpLoading(true);
    setExpError(null);
    const filter: IExpenseFilter = {};
    if (expFilterMode === "mine" && myUserId) filter.spentByUserId = myUserId;
    if (expFilterReimbursed !== "") filter.isReimbursed = expFilterReimbursed === "true";
    if (expFilterFrom) filter.from = expFilterFrom;
    if (expFilterTo) filter.to = expFilterTo;
    getExpenses(filter)
      .then((res) => setExpenses(res.data ?? []))
      .catch(() => setExpError("Faturalar yüklenemedi."))
      .finally(() => setExpLoading(false));
  };

  useEffect(() => {
    if (pageTab === "sahis") {
      setExpLoading(true);
      setExpError(null);
      getExpenses({})
        .then((res) => setExpenses(res.data ?? []))
        .catch(() => setExpError("Faturalar yüklenemedi."))
        .finally(() => setExpLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTab]);

  const inputStyle: React.CSSProperties = { borderRadius: "10px", border: "1.5px solid #dee2e6" };

  const tabs = [
    { id: "add"  as TabId, label: "Fatura Ekle",          icon: "bi-plus-circle-fill", color: "#3b82f6" },
    { id: "list" as TabId, label: "Faturaları Görüntüle", icon: "bi-receipt-cutoff",   color: "#6366f1" },
  ];

  return (
    <div className="container py-4" style={{ maxWidth: "1100px" }}>

      {/* Başlık */}
      <div className="d-flex align-items-center mb-3">
        <div style={{ width: "5px", height: "32px", background: "#3b82f6", borderRadius: "4px", marginRight: "12px" }} />
        <h4 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
          <i className="bi bi-receipt-cutoff me-2 text-primary"></i>Fatura Yönetimi
        </h4>
      </div>

      {/* Sayfa Sekmeleri */}
      <div className="d-flex gap-2 mb-4">
        {([
          { id: "kurumsal" as const, label: "Kurumsal Faturalar", icon: "bi-building" },
          { id: "sahis"    as const, label: "Şahıs Faturaları",   icon: "bi-person-fill" },
        ] as const).map((t) => (
          <button key={t.id} onClick={() => setPageTab(t.id)}
            className="btn"
            style={{
              borderRadius: "10px", fontWeight: 600, fontSize: "0.9rem",
              background: pageTab === t.id ? "linear-gradient(135deg,#3b82f6,#2563eb)" : "#f1f5f9",
              color: pageTab === t.id ? "#fff" : "#64748b",
              border: "none", padding: "9px 20px",
              boxShadow: pageTab === t.id ? "0 2px 8px rgba(59,130,246,0.35)" : "none",
            }}>
            <i className={`bi ${t.icon} me-2`}></i>{t.label}
          </button>
        ))}
      </div>

      {pageTab === "kurumsal" && (<>
      <RestaurantSelector
        restaurantList={restaurantList}
        selectedId={selectedRestaurantId}
        onChange={setSelectedRestaurantId}
        selectedRestaurant={selectedRestaurant}
      />

      {!selectedRestaurantId && (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-building" style={{ fontSize: "2.5rem", opacity: 0.3 }}></i>
          <div className="mt-2 small">Devam etmek için bir restoran seçin.</div>
        </div>
      )}

      {selectedRestaurantId && (
        <>
          {/* Sekmeler */}
          <div className="d-flex gap-2 mb-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="btn d-flex align-items-center gap-2"
                style={{
                  borderRadius: "12px", fontWeight: 600, fontSize: "0.88rem", padding: "8px 20px",
                  border: `2px solid ${activeTab === tab.id ? tab.color : "#e5e7eb"}`,
                  background: activeTab === tab.id ? tab.color : "#fff",
                  color: activeTab === tab.id ? "#fff" : "#6b7280",
                  boxShadow: activeTab === tab.id ? `0 3px 10px ${tab.color}44` : "none",
                  transition: "all 0.15s ease",
                }}
              >
                <i className={`bi ${tab.icon}`}></i>{tab.label}
              </button>
            ))}
          </div>

          {/* ── Fatura Ekle ── */}
          {activeTab === "add" && (
            <div className="row justify-content-center">
              <div className="col-md-7">
                <div className="p-4 rounded-3" style={{ background: "#fff", border: "1.5px solid #e5e7eb", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                  <div className="fw-semibold mb-4" style={{ color: "#374151", fontSize: "0.82rem" }}>
                    <i className="bi bi-plus-circle-fill me-2 text-primary"></i>YENİ FATURA
                  </div>
                  <form onSubmit={handleAddBill}>
                    <div className="d-flex flex-column gap-3">
                      <div>
                        <label className="form-label small fw-semibold text-muted mb-1">
                          <i className="bi bi-cash-coin me-1"></i>Tutar (₺)
                        </label>
                        <input type="number" className="form-control" style={inputStyle} placeholder="0.00" value={billAmount} onChange={(e) => setBillAmount(e.target.value)} required min={0} step="0.01" />
                      </div>
                      <div>
                        <label className="form-label small fw-semibold text-muted mb-1">
                          <i className="bi bi-card-text me-1"></i>Açıklama
                        </label>
                        <input type="text" className="form-control" style={inputStyle} placeholder="Fatura açıklaması..." value={billDescription} onChange={(e) => setBillDescription(e.target.value)} />
                      </div>
                      <div>
                        <label className="form-label small fw-semibold text-muted mb-1">
                          <i className="bi bi-calendar-event me-1"></i>Fatura Tarihi
                        </label>
                        <Flatpickr value={billDate} onChange={(dates) => dates[0] && setBillDate(dates[0])} options={{ dateFormat: "d-m-Y", disableMobile: true }} className="form-control" style={inputStyle} />
                      </div>

                      {addError && (
                        <div className="p-2 rounded-2" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: "0.82rem" }}>
                          <i className="bi bi-exclamation-circle me-1"></i>{addError}
                        </div>
                      )}
                      {addSuccess && (
                        <div className="p-2 rounded-2" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", fontSize: "0.82rem" }}>
                          <i className="bi bi-check-circle-fill me-1"></i>Fatura başarıyla eklendi.
                        </div>
                      )}

                      <button type="submit" disabled={addLoading} className="btn" style={{ borderRadius: "10px", background: "linear-gradient(135deg,#3b82f6,#1d4ed8)", color: "#fff", fontWeight: 600, border: "none", boxShadow: "0 3px 10px rgba(59,130,246,0.3)", padding: "10px" }}>
                        {addLoading
                          ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Ekleniyor...</>
                          : <><i className="bi bi-plus-lg me-1"></i>Fatura Ekle</>}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* ── Fatura Listesi ── */}
          {activeTab === "list" && (
            <>
              {/* Filtreler */}
              <div className="p-3 rounded-3 mb-4 d-flex flex-wrap align-items-end gap-3" style={{ background: "#f8faff", border: "1.5px solid #e0eaff" }}>
                <div>
                  <label className="form-label small fw-semibold mb-1 text-muted d-block"><i className="bi bi-calendar3 me-1"></i>Ay</label>
                  <select
                    className="form-select"
                    style={{ ...inputStyle, minWidth: "130px", fontSize: "0.9rem" }}
                    value={month ?? ""}
                    onChange={(e) => setMonth(e.target.value === "" ? null : Number(e.target.value))}
                  >
                    <option value="">Hepsi</option>
                    {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label small fw-semibold mb-1 text-muted d-block"><i className="bi bi-calendar3 me-1"></i>Yıl</label>
                  <input type="number" className="form-control" style={{ ...inputStyle, minWidth: "100px", fontSize: "0.9rem" }} value={year} onChange={(e) => setYear(Number(e.target.value))} min={2020} max={2100} />
                </div>
                <div>
                  <label className="form-label small fw-semibold mb-1 text-muted d-block"><i className="bi bi-cash me-1"></i>Ödeme</label>
                  <select className="form-select" style={{ ...inputStyle, minWidth: "130px", fontSize: "0.9rem" }} value={filterPaid} onChange={(e) => setFilterPaid(e.target.value)}>
                    <option value="">Hepsi</option>
                    <option value="true">Ödendi</option>
                    <option value="false">Ödenmedi</option>
                  </select>
                </div>
                <div>
                  <label className="form-label small fw-semibold mb-1 text-muted d-block"><i className="bi bi-toggle-on me-1"></i>Durum</label>
                  <select className="form-select" style={{ ...inputStyle, minWidth: "120px", fontSize: "0.9rem" }} value={filterEnabled} onChange={(e) => setFilterEnabled(e.target.value)}>
                    <option value="">Hepsi</option>
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                  </select>
                </div>
                <button onClick={fetchBills} disabled={listLoading} className="btn" style={{ borderRadius: "10px", background: "linear-gradient(135deg,#3b82f6,#1d4ed8)", color: "#fff", fontWeight: 600, border: "none", boxShadow: "0 3px 10px rgba(59,130,246,0.3)", padding: "8px 24px", fontSize: "0.9rem" }}>
                  {listLoading
                    ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Yükleniyor...</>
                    : <><i className="bi bi-search me-1"></i>Ara</>}
                </button>
              </div>

              {listError && (
                <div className="d-flex align-items-center gap-2 rounded-3 px-4 py-3 mb-3" style={{ background: "#fff7ed", border: "1.5px solid #fed7aa", color: "#92400e", fontSize: "0.9rem" }}>
                  <i className="bi bi-inbox fs-5"></i><span>{listError}</span>
                </div>
              )}

              {billList.length > 0 ? (
                <>
                  <div className="d-flex align-items-center mb-3">
                    <div style={{ width: "5px", height: "22px", background: "#6366f1", borderRadius: "3px", marginRight: "10px" }} />
                    <span className="fw-bold" style={{ color: "#6366f1", fontSize: "0.88rem" }}>{month ? `${MONTHS[month - 1]} ${year}` : String(year)} Faturaları</span>
                    <span className="badge ms-2" style={{ background: "#6366f1", fontSize: "0.72rem", borderRadius: "20px" }}>{billList.length} kayıt</span>
                  </div>
                  {billList.map((bill) => (
                    <BillCard key={bill.billNo} bill={bill} onEdit={() => setEditBill(bill)} />
                  ))}
                </>
              ) : (
                !listLoading && !listError && (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-receipt" style={{ fontSize: "2.5rem", opacity: 0.3 }}></i>
                    <div className="mt-2 small">Listeyi görmek için filtreleri seçip Ara'ya basın.</div>
                  </div>
                )
              )}
            </>
          )}
        </>
      )}

      <EditBillModal bill={editBill} restaurantId={selectedRestaurantId} onClose={() => setEditBill(null)} onSaved={fetchBills} />
      </>)}

      {pageTab === "sahis" && (
        <>
          {/* Filtreler */}
          <div className="card mb-4" style={{ borderRadius: "14px", border: "1.5px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <div className="card-body pb-3">
              <div className="d-flex flex-wrap gap-2 mb-3">
                {([
                  { id: "all"  as const, label: "Tümü" },
                  { id: "mine" as const, label: "Kendi Faturalarım" },
                ] as const).map((m) => (
                  <button key={m.id} onClick={() => setExpFilterMode(m.id)}
                    className="btn btn-sm"
                    style={{
                      borderRadius: "8px", fontWeight: 600, fontSize: "0.83rem",
                      background: expFilterMode === m.id ? "#6366f1" : "#f1f5f9",
                      color: expFilterMode === m.id ? "#fff" : "#64748b",
                      border: "none", padding: "6px 14px",
                    }}>
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="row g-2 align-items-end">
                <div className="col-12 col-sm-auto">
                  <label className="form-label small fw-semibold text-muted mb-1">Geri Ödeme</label>
                  <select className="form-select form-select-sm" style={{ borderRadius: "8px", minWidth: "145px" }}
                    value={expFilterReimbursed}
                    onChange={(e) => setExpFilterReimbursed(e.target.value as "" | "true" | "false")}>
                    <option value="">Tümü</option>
                    <option value="true">Ödendi</option>
                    <option value="false">Ödenmedi</option>
                  </select>
                </div>
                <div className="col-6 col-sm-auto">
                  <label className="form-label small fw-semibold text-muted mb-1">Başlangıç</label>
                  <input type="date" className="form-control form-control-sm" style={{ borderRadius: "8px", minWidth: "145px" }}
                    value={expFilterFrom} onChange={(e) => setExpFilterFrom(e.target.value)} />
                </div>
                <div className="col-6 col-sm-auto">
                  <label className="form-label small fw-semibold text-muted mb-1">Bitiş</label>
                  <input type="date" className="form-control form-control-sm" style={{ borderRadius: "8px", minWidth: "145px" }}
                    value={expFilterTo} onChange={(e) => setExpFilterTo(e.target.value)} />
                </div>
                <div className="col-12 col-sm-auto">
                  <button className="btn btn-sm btn-primary w-100" style={{ borderRadius: "8px", fontWeight: 600, minWidth: "80px" }}
                    onClick={fetchExpenses} disabled={expLoading}>
                    {expLoading
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="bi bi-search me-1"></i>Ara</>}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Fatura Ekle */}
          <div className="d-flex flex-wrap justify-content-end gap-2 mb-3">
            <button className="btn" onClick={() => setShowVendorManager(true)}
              style={{ borderRadius: "10px", fontWeight: 600, fontSize: "0.88rem", background: "#fffbeb", color: "#b45309", padding: "9px 20px", border: "1.5px solid #fde68a" }}>
              <i className="bi bi-building me-2"></i>Firmalar
            </button>
            <button className="btn" onClick={() => setShowAddExpense(true)}
              style={{ borderRadius: "10px", fontWeight: 600, fontSize: "0.88rem", background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", padding: "9px 20px", border: "none", boxShadow: "0 2px 8px rgba(99,102,241,0.3)" }}>
              <i className="bi bi-plus-circle me-2"></i>Fatura Ekle
            </button>
          </div>

          {/* Hata */}
          {expError && (
            <div className="d-flex align-items-center gap-2 rounded-3 px-4 py-3 mb-3"
              style={{ background: "#fff7ed", border: "1.5px solid #fed7aa", color: "#92400e", fontSize: "0.9rem" }}>
              <i className="bi bi-exclamation-triangle-fill"></i><span>{expError}</span>
            </div>
          )}

          {/* Yükleniyor */}
          {expLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" />
            </div>
          )}

          {/* Boş */}
          {!expLoading && !expError && expenses.length === 0 && (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-receipt" style={{ fontSize: "2.5rem", opacity: 0.3 }}></i>
              <div className="mt-2 small">Fatura bulunamadı.</div>
            </div>
          )}

          {/* Kart Listesi — bu ay öne, 1 aydan eski geçmiş harcamalar altta */}
          {!expLoading && expenses.length > 0 && (() => {
            const now = new Date();
            const isCurrentMonth = (dateStr: string) => {
              const d = new Date(dateStr);
              return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
            };
            const currentMonthExpenses = expenses.filter((e) => isCurrentMonth(e.spentAt));
            const pastExpenses = expenses.filter((e) => !isCurrentMonth(e.spentAt));
            return (
              <>
                {currentMonthExpenses.length > 0 && (
                  <div className="row g-3">
                    {currentMonthExpenses.map((exp) => (
                      <ExpenseCard key={exp.id} exp={exp} onClick={() => setSelectedExpense(exp)} />
                    ))}
                  </div>
                )}
                {pastExpenses.length > 0 && (
                  <>
                    <div className="d-flex align-items-center gap-2 mt-4 mb-3">
                      <i className="bi bi-clock-history text-muted"></i>
                      <span className="fw-semibold text-muted" style={{ fontSize: "0.85rem" }}>Geçmiş Harcamalar</span>
                      <span className="badge" style={{ background: "#f1f5f9", color: "#64748b", fontSize: "0.72rem" }}>{pastExpenses.length}</span>
                    </div>
                    <div className="row g-3">
                      {pastExpenses.map((exp) => (
                        <ExpenseCard key={exp.id} exp={exp} onClick={() => setSelectedExpense(exp)} />
                      ))}
                    </div>
                  </>
                )}
              </>
            );
          })()}

          {/* Özet ve Grafikler */}
          {!expLoading && !expError && (
            <div className="mt-5">
              <ExpenseSummary expenses={expenses} />
            </div>
          )}

          {showAddExpense && myUserId && (
            <AddExpenseModal
              myUserId={myUserId}
              myDisplayName=""
              onClose={() => setShowAddExpense(false)}
              onCreated={fetchExpenses}
            />
          )}
          {selectedExpense && (
            <ExpenseDetailModal
              expense={selectedExpense}
              onClose={() => setSelectedExpense(null)}
              onRefresh={() => { fetchExpenses(); setSelectedExpense(null); }}
            />
          )}
          {showVendorManager && (
            <VendorManagerModal onClose={() => setShowVendorManager(false)} onChange={fetchExpenses} />
          )}
        </>
      )}
    </div>
  );
}

export default AddBillPage;